import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TEMPERATURES, fmtEuro } from "@/lib/crm";
import { UserAvatar } from "@/components/crm/Badges";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Loader2, Users, UserPlus, CalendarCheck, Ban, FileText, Trophy, AlertTriangle,
  TrendingUp, Wallet, XCircle, Percent, Briefcase,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import { cn } from "@/lib/utils";
import AssignmentMonitor from "@/components/crm/AssignmentMonitor";
import { usePageState } from "@/hooks/usePageState";

const PERIODS = [
  { k: "today", l: "Oggi" }, { k: "week", l: "Settimana" }, { k: "month", l: "Mese" },
  { k: "quarter", l: "Trimestre" }, { k: "year", l: "Anno" }, { k: "custom", l: "Personalizzato" },
  { k: "all", l: "Sempre" },
];

export default function Reports() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [perf, setPerf] = useState([]);
  const [period, setPeriod] = usePageState("report_period", "month");
  const [custom, setCustom] = usePageState("report_custom", { start: "", end: "" });
  const [filters, setFilters] = usePageState("report_filters", { assigned_to: "", interest: "", fonte: "", sorgente: "", campaign: "" });
  const [users, setUsers] = useState([]);
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const buildParams = useCallback((withUser) => {
    const p = { period };
    if (period === "custom" && custom.start && custom.end) {
      p.start = new Date(custom.start).toISOString();
      p.end = new Date(custom.end).toISOString();
    }
    Object.entries(filters).forEach(([k, v]) => { if (v && (withUser || k !== "assigned_to")) p[k] = v; });
    return p;
  }, [period, custom, filters]);

  const loadReports = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [dash, performance] = await Promise.all([
        api.get("/dashboard/admin", { params: buildParams(true) }),
        api.get("/reports/performance", { params: buildParams(false) }),
      ]);
      setData(dash.data);
      setPerf(Array.isArray(performance.data) ? performance.data : []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Impossibile caricare i report. Riprova.");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    if (!isAdmin) return;
    loadReports();
  }, [loadReports, isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role === "commerciale"))).catch(() => {});
    api.get("/interests").then((r) => setInterests(r.data)).catch(() => {});
  }, [isAdmin]);

  if (!isAdmin) return <CommercialeReport />;

  if (error) {
    return (
      <div className="space-y-4 animate-fade-up">
        <h1 className="font-heading text-2xl md:text-3xl font-black tracking-tight">Report</h1>
        <div data-testid="report-error" className="rounded-lg border border-destructive/30 bg-destructive/10 p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-destructive mx-auto mb-2" />
          <p className="text-sm text-destructive mb-4">{error}</p>
          <button onClick={loadReports} className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium">Riprova</button>
        </div>
      </div>
    );
  }

  if (loading || !data) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" data-testid="report-loader" /></div>;

  const d = data.deals || { open_count: 0, open_value: 0, won_count: 0, won_value: 0, lost_count: 0, lost_value: 0, close_rate: 0 };

  const dealCards = [
    { label: "Valore trattative aperte", value: fmtEuro(d.open_value), sub: `${d.open_count} aperte`, icon: Wallet, color: "text-blue-500", bg: "bg-blue-500/10" },
    { label: "Valore vinto nel periodo", value: fmtEuro(d.won_value), sub: `${d.won_count} vinte`, icon: Trophy, color: "text-emerald-500", bg: "bg-emerald-500/10" },
    { label: "Valore perso nel periodo", value: fmtEuro(d.lost_value), sub: `${d.lost_count} perse`, icon: XCircle, color: "text-red-500", bg: "bg-red-500/10" },
    { label: "Tasso di chiusura", value: `${d.close_rate}%`, sub: "solo trattative chiuse", icon: Percent, color: "text-violet-500", bg: "bg-violet-500/10" },
  ];

  const kpis = [
    { label: "Lead totali", value: data.total, icon: Users, color: "text-sky-500" },
    { label: "Nuovi", value: data.nuovi, icon: UserPlus, color: "text-blue-500" },
    { label: "Da assegnare", value: data.da_assegnare, icon: UserPlus, color: "text-violet-500" },
    { label: "Appuntamenti", value: data.appuntamenti, icon: CalendarCheck, color: "text-emerald-500" },
    { label: "No-show", value: data.no_show, icon: Ban, color: "text-amber-500" },
    { label: "Preventivi", value: data.preventivi, icon: FileText, color: "text-purple-500" },
    { label: "Clienti chiusi", value: data.clienti, icon: Trophy, color: "text-yellow-500" },
    { label: "Follow-up scaduti", value: data.followup_scaduti, icon: AlertTriangle, color: "text-red-500" },
  ];

  const tempData = Object.entries(data.by_temperature).map(([k, v]) => ({ name: TEMPERATURES[k]?.label || k, value: v, color: TEMPERATURES[k]?.color || "#999" }));
  const statusData = Object.entries(data.by_status).map(([k, v]) => ({ name: k, value: v }));
  const sorgenteOpts = Object.keys(data.by_sorgente || {}).filter((k) => k && k !== "None");
  const campaignOpts = Object.keys(data.by_campaign || {}).filter((k) => k && k !== "None");

  return (
    <div className="space-y-5 animate-fade-up">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-black tracking-tight">Report</h1>
        <p className="text-sm text-muted-foreground">Dashboard amministratore · valore economico e performance</p>
      </div>

      {/* Period + filters */}
      <div className="space-y-3">
        <div className="flex rounded-md border overflow-hidden w-fit flex-wrap">
          {PERIODS.map((p) => (
            <button key={p.k} data-testid={`period-${p.k}`} onClick={() => setPeriod(p.k)} className={cn("px-3 py-1.5 text-sm transition-colors", period === p.k ? "bg-primary text-primary-foreground" : "hover:bg-accent")}>{p.l}</button>
          ))}
        </div>
        {period === "custom" && (
          <div className="flex gap-2 items-end">
            <div><Label className="text-xs">Da</Label><Input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} className="h-8" data-testid="custom-start" /></div>
            <div><Label className="text-xs">A</Label><Input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} className="h-8" data-testid="custom-end" /></div>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <RFilter label="Commerciale" value={filters.assigned_to} onChange={(v) => setFilters((f) => ({ ...f, assigned_to: v }))} options={users.map((u) => u.id)} labels={users.reduce((a, u) => ({ ...a, [u.id]: u.name }), {})} />
          <RFilter label="Interesse" value={filters.interest} onChange={(v) => setFilters((f) => ({ ...f, interest: v }))} options={interests.map((i) => i.name)} />
          <RFilter label="Fonte" value={filters.fonte} onChange={(v) => setFilters((f) => ({ ...f, fonte: v }))} options={["Meta Ads", "Google Ads", "Sito", "Referral", "Manuale"]} />
          <RFilter label="Sorgente" value={filters.sorgente} onChange={(v) => setFilters((f) => ({ ...f, sorgente: v }))} options={sorgenteOpts} />
          <RFilter label="Campagna" value={filters.campaign} onChange={(v) => setFilters((f) => ({ ...f, campaign: v }))} options={campaignOpts} />
        </div>
      </div>

      {/* Deal value cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {dealCards.map((k) => (
          <div key={k.label} data-testid={`deal-card-${k.label}`} className="rounded-lg border bg-card p-4">
            <div className={cn("w-9 h-9 rounded-md grid place-items-center mb-2", k.bg, k.color)}><k.icon className="w-5 h-5" /></div>
            <div className="font-heading text-2xl font-black tabular-nums">{k.value}</div>
            <div className="text-xs text-muted-foreground">{k.label}</div>
            <div className="text-[11px] text-muted-foreground/70 mt-0.5">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Lead KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpis.map((k) => (
          <div key={k.label} data-testid={`kpi-${k.label}`} className="rounded-lg border bg-card p-4">
            <k.icon className={cn("w-5 h-5 mb-2", k.color)} />
            <div className="font-heading text-2xl font-black tabular-nums">{k.value}</div>
            <div className="text-xs text-muted-foreground">{k.label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <ChartCard title="Lead per stato">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={statusData} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="value" fill="#3B82F6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Lead per temperatura">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={tempData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => `${e.name}: ${e.value}`} labelLine={false} fontSize={11}>
                {tempData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Lead per fonte"><MiniBars obj={data.by_fonte} /></ChartCard>
        <ChartCard title="Lead per commerciale"><MiniBars obj={data.by_user} /></ChartCard>
      </div>

      {/* Performance table */}
      <div className="rounded-lg border bg-card overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center gap-2"><Briefcase className="w-4 h-4 text-muted-foreground" /><h2 className="font-heading font-bold">Performance commerciali</h2><span className="text-xs text-muted-foreground ml-2">Assegnati / Lavorati per data di assegnazione CRM</span></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead><tr className="border-b text-xs text-muted-foreground text-left">
              <th className="px-4 py-2 font-medium">Commerciale</th>
              <th className="px-3 py-2 font-medium text-center">Assegnati</th>
              <th className="px-3 py-2 font-medium text-center">Lavorati</th>
              <th className="px-3 py-2 font-medium text-center">Aperte</th>
              <th className="px-3 py-2 font-medium text-right">Valore aperto</th>
              <th className="px-3 py-2 font-medium text-center">Vinti</th>
              <th className="px-3 py-2 font-medium text-right">Valore vinti</th>
              <th className="px-3 py-2 font-medium text-center">Persi</th>
              <th className="px-3 py-2 font-medium text-right">Valore perso</th>
              <th className="px-3 py-2 font-medium text-center">Tasso chiusura</th>
            </tr></thead>
            <tbody>
              {perf.map((p) => (
                <tr key={p.user.id} data-testid={`perf-row-${p.user.id}`} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="px-4 py-2.5"><div className="flex items-center gap-2"><UserAvatar user={p.user} size={24} />{p.user.name}</div></td>
                  <td className="px-3 py-2.5 text-center tabular-nums" data-testid={`perf-assegnati-${p.user.id}`}>{p.assegnati}</td>
                  <td className="px-3 py-2.5 text-center tabular-nums" data-testid={`perf-lavorati-${p.user.id}`}>{p.lavorati}</td>
                  <td className="px-3 py-2.5 text-center tabular-nums">{p.trattative_aperte}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmtEuro(p.valore_aperto)}</td>
                  <td className="px-3 py-2.5 text-center tabular-nums text-emerald-600 dark:text-emerald-400 font-semibold">{p.vinte}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-emerald-600 dark:text-emerald-400">{fmtEuro(p.valore_vinto)}</td>
                  <td className="px-3 py-2.5 text-center tabular-nums text-red-500">{p.perse}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-red-500">{fmtEuro(p.valore_perso)}</td>
                  <td className="px-3 py-2.5 text-center tabular-nums font-bold">{p.tasso_chiusura}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AssignmentMonitor navigate={navigate} isAdmin stateKey="report" />
    </div>
  );
}

function RFilter({ label, value, onChange, options, labels = {} }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select value={value || "__all"} onValueChange={(v) => onChange(v === "__all" ? "" : v)}>
        <SelectTrigger className="h-8 text-xs" data-testid={`report-filter-${label}`}><SelectValue placeholder="Tutti" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__all">Tutti</SelectItem>
          {options.map((o) => <SelectItem key={o} value={o}>{labels[o] || o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function ChartCard({ title, children }) {
  return <div className="rounded-lg border bg-card p-4"><h3 className="font-heading font-bold text-sm mb-3">{title}</h3>{children}</div>;
}

function MiniBars({ obj }) {
  const entries = Object.entries(obj || {}).filter(([k]) => k !== "None" && k !== "null").sort((a, b) => b[1] - a[1]);
  const max = Math.max(...entries.map((e) => e[1]), 1);
  return (
    <div className="space-y-2">
      {entries.map(([k, v]) => (
        <div key={k} className="flex items-center gap-2 text-xs">
          <span className="w-28 truncate text-muted-foreground">{k}</span>
          <div className="flex-1 h-4 bg-muted rounded overflow-hidden"><div className="h-full bg-primary rounded" style={{ width: `${(v / max) * 100}%` }} /></div>
          <span className="w-6 text-right tabular-nums">{v}</span>
        </div>
      ))}
    </div>
  );
}

function CommercialeReport() {
  const [perf, setPerf] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { api.get("/dashboard/commerciale").then((r) => setPerf(r.data)); }, []);
  if (!perf) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  const c = perf.counts;
  const stats = [
    { label: "Priorità alta", value: c.priorita_alta }, { label: "Follow-up oggi", value: c.followup_oggi },
    { label: "Follow-up in ritardo", value: c.followup_ritardo }, { label: "Nuovi lead", value: c.nuovi_lead },
    { label: "Appuntamenti oggi", value: c.appuntamenti_oggi }, { label: "No-show", value: c.no_show },
  ];
  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-black tracking-tight">I miei risultati</h1>
          <p className="text-sm text-muted-foreground">Riepilogo attività di {user?.name}</p>
        </div>
        <button data-testid="open-my-performance" onClick={() => navigate(`/performance/${user?.id}`)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity">
          <TrendingUp className="w-4 h-4" /> Dashboard performance
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="rounded-lg border bg-card p-5">
            <div className="font-heading text-3xl font-black tabular-nums">{s.value}</div>
            <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
          </div>
        ))}
      </div>
      <AssignmentMonitor navigate={navigate} isAdmin={false} stateKey="report" />
    </div>
  );
}
